import { client, getApiErrorMessage, stringifyQuery } from "utils/api";
import { toastr } from "react-redux-toastr";

export const compacted = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => {
      if (value == null) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );

export const getHeadStyle = (head) => {
  const styles = {};
  if (
    [
      "average_cpc",
      "sold_amount",
      "cost",
      "total_clicks",
      "total_views",
      "cpc",
      "roas",
    ].includes(head.id)
  ) {
    styles["minWidth"] = "70px";
    styles["width"] = "70px";
  }

  return styles;
};

export const getRowStyle = ({
  depth,
  original: { kind, status, offers_count, record_type },
}) => {
  const styles = {};
  if (depth === 0) {
    styles["backgroundColor"] = "rgba(205, 205, 205, 0.8)";
    styles["cursor"] = "pointer";

    if (status === "PAUSED") {
      styles["opacity"] = 0.6;
    }
  }
  if (depth === 1) {
    if (kind === "best_sellers") {
      styles["backgroundColor"] = "rgba(136, 215, 77, 0.2)";
    }
    if (kind === "standard") {
      styles["backgroundColor"] = "rgba(244, 176, 79, 0.2)";
    }
    if (kind === "high_budget") {
      styles["backgroundColor"] = "rgba(244, 79, 130, 0.2)";
    }
    if (kind === "neutral") {
      styles["backgroundColor"] = "rgba(230, 230, 230, 0.5)";
    }

    styles["cursor"] = "pointer";
  }
  if (depth === 2) {
    styles["backgroundColor"] = "#fff";
    if (
      offers_count < 1 ||
      record_type === "channel_campaign_group_offer_stat"
    ) {
      styles["opacity"] = "0.5";
    }
  }
  return styles;
};

export const getCellStyle = ({ column }) => {
  const styles = {};
  if (
    [
      "progress_status",
      "specification_product_group_workflow",
      "specification_product_workflow",
      "description_workflow",
      "image_workflow",
      "title_workflow",
      "image_assign_workflow",
      "export_workflow",
    ].includes(column.id)
  ) {
    styles["textAlign"] = "center";
  }
  return styles;
};

export const getData = async ({ channel_id, query_filters = {}, page = 1 }) => {
  try {
    const search = Object.keys(query_filters).reduce((acc, key) => {
      if (key.includes("_matches")) {
        acc[key] = `%25${query_filters[key]}%25`;
      } else {
        acc[key] = query_filters[key];
      }
      return acc;
    }, {});

    return await client.get(
      `/channels/${channel_id}/channel_campaigns?${stringifyQuery({
        ...search,
        per_page: 100,
        page,
        context: "ui_index",
        "sort[column]": "name",
        "sort[order]": "asc",
      })}`,
    );
  } catch (error) {
    toastr.error("Błąd", getApiErrorMessage(error));
    throw error;
  }
};

export const getRowData = async ({
  channel_id,
  channel_campaign_id,
  channel_campaign_group_id,
  type,
  show_archived,
}) => {
  try {
    if (type === "channel_campaign") {
      const { data } = await client.get(
        `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}?context=${show_archived ? "ui_campaign_list" : "ui_active_campaign_list"}`,
      );
      return data;
    }
    if (type === "channel_campaign_group") {
      const {
        data: { channel_campaign_group_products },
      } = await client.get(
        `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}?context=ui_campaign_list`,
      );

      const channel_campaign_group_products_with_offers = [
        ...channel_campaign_group_products,
      ]
        .filter(({ offers_count }) => offers_count > 0)
        .sort((a, b) => a.product?.name.localeCompare(b?.product?.name));

      const channel_campaign_group_products_without_offers = [
        ...channel_campaign_group_products,
      ]
        .filter(({ offers_count }) => offers_count === 0)
        .sort((a, b) => a.product?.name.localeCompare(b?.product?.name));

      return {
        channel_campaign_group_products_with_offers,
        channel_campaign_group_products_without_offers,
      };
    }
    if (type === "archival_offers_channel_campaign_group") {
      const {
        data: { channel_campaign_group_offer_stats },
      } = await client.get(
        `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}/phantom_products`,
      );

      const channel_campaign_group_offer_stats_sorted =
        channel_campaign_group_offer_stats.sort((a, b) =>
          a.product?.name.localeCompare(b?.product?.name),
        );

      return { channel_campaign_group_offer_stats_sorted };
    }
    if (type === "waiting_channel_campaign_group_products") {
      const {
        data: { products },
      } = await client.get(
        `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}/waiting_products`,
      );

      const products_sorted = products.sort((a, b) =>
        a.product?.name.localeCompare(b?.product?.name),
      );

      return { products_sorted };
    }
  } catch (error) {
    toastr.error("Błąd", getApiErrorMessage(error));
    throw error;
  }
};

export const moveChannelCampaignGroupProduct = async ({
  channel_id,
  channel_campaign_id,
  channel_campaign_group_id,
  new_channel_campaign_group_id,
  channel_campaign_group_product_id,
}) => {
  try {
    await client.post(
      `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}/channel_campaign_group_products/${channel_campaign_group_product_id}/move`,
      {
        new_channel_campaign_group_id,
      },
    );
    toastr.success("Sukces", "Przeniesiono pomyślnie");
  } catch (error) {
    toastr.error("Błąd", getApiErrorMessage(error));
    throw error;
  }
};

export const removeChannelCampaignGroupProduct = async ({
  channel_id,
  channel_campaign_id,
  channel_campaign_group_id,
  channel_campaign_group_product_id,
}) => {
  try {
    await client.delete(
      `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}/channel_campaign_group_products/${channel_campaign_group_product_id}`,
    );
    toastr.success("Sukces", "Usunięto pomyślnie");
  } catch (error) {
    toastr.error("Błąd", getApiErrorMessage(error));
    throw error;
  }
};
export const addChannelCampaignGroupProduct = async ({
  channel_id,
  channel_campaign_id,
  channel_campaign_group_id,
  product_id,
}) => {
  try {
    await client.post(
      `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}/channel_campaign_group_products`,
      {
        product_ids: [product_id],
      },
    );
    toastr.success("Sukces", "Dodano pomyślnie");
  } catch (error) {
    toastr.error("Błąd", getApiErrorMessage(error));
    throw error;
  }
};

export const exportToAllegro = async ({
  channel_id,
  channel_campaign_id,
  channel_campaign_group_id,
}) => {
  try {
    await client.post(
      `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}/export`,
    );
    toastr.success("Sukces", "Wyexportowano pomyślnie");
  } catch (error) {
    toastr.error("Błąd", getApiErrorMessage(error));
    throw error;
  }
};
